import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';
import UserCache from '../../services/cache/UserCache';
import LogicBaseCollectionCache from '../../services/cache/LogicBaseCollectionCache';
import UserAccount from '../../services/UserAccount';
import LogicBaseCollectionSubscriptionCache from '../../services/cache/LogicBaseCollectionSubscriptionCache';
import LogicBaseUserCache from '../../services/cache/LogicBaseUserCache';
import LogicBaseRelationshipCache from '../../services/cache/LogicBaseRelationshipCache';
import { getAppLikes, getAppPins, getCollectionSubscribers, getCommentLikes, getUserFollowees, getUserFollowers } from '../../services/Pagination';
import UserProfileHeader from '../UserProfileHeader';
import Scrollable from '../Scrollable';
import Popup from '../Popup';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import ConfirmDialog from '../ConfirmDialog';
import LoadingIndicator from '../LoadingIndicator';
import { ModalSheetTitle } from '../../styledhtml/Tailwind';
const Shell = styled.div`
  width: min(100vw - 2rem, 520px);
  max-width: 100%;
  height: min(85vh, 640px);
  max-height: min(85vh, 640px);
  margin: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: ${Constants.SurfaceColor};
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: 1.25rem 1.25rem 0.85rem;
  border-bottom: ${Constants.BottomBorderDivider};
  background: ${Constants.SurfaceColor};
`;

const ListWrap = styled.div`
  padding: 0.75rem 1.25rem 1.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const UserRowCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 1rem;
  border: ${Constants.LightBorder};
  border-radius: ${Constants.BoxRadius}px;
  background: ${Constants.SurfaceColor};
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: ${Constants.CTAShadow};
  }
`;

const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const RemoveButton = styled.button.attrs({ type: 'button' })`
  flex-shrink: 0;
  font-family: inherit;
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 600;
  color: ${Constants.DarkTextColor};
  background: ${Constants.SurfaceColor};
  border: 1px solid ${Constants.DarkTextColor};
  border-radius: 999px;
  padding: 0.45rem 0.95rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${Constants.StaticGray};
  }

  ${Constants.MediaMobile} {
    font-size: ${Constants.SmallFontSize};
    padding: 0.4rem 0.75rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem 1rem;
  color: ${Constants.MediumTextGray};
  font-size: ${Constants.SemiSmallFontSize};
  line-height: 1.5;
`;

export default class LogicBaseUsersPopup extends LogictryComponent {
  static propTypes = {
    onClose: PropTypes.func,
    content: PropTypes.string,
    id: PropTypes.string,
  }
  state = {
    showRemoveSubscriberPopup: false,
    showRemoveFollowerPopup: false,
  }
  componentDidMount() {
    UserAccount.onStateUpdate(this);
    UserCache.onStateUpdate(this);
    LogicBaseCollectionCache.onStateUpdate(this);
  }
  componentWillUnmount() {
    UserAccount.offStateUpdate(this);
    UserCache.offStateUpdate(this);
    LogicBaseCollectionCache.offStateUpdate(this);
    if (this.cache) this.cache.offStateUpdate(this);
  }
  onPagination = () => {
    if (this.cache.showMore && this.cache.allFound && this.cache.initialized) {
      this.cache.getMore();
    }
  }
  render() {
    const { onClose, content, id } = this.props;
    const { showRemoveSubscriberPopup, showRemoveFollowerPopup } = this.state;
    const { isLoggedIn, account } = UserAccount;

    let title;
    let users;
    let noneFoundText;
    let collection;
    let canRemoveSubscriber = false;
    let followee;
    if (content === 'CommentLikes') {
      if (!this.cache) {
        this.cache = getCommentLikes(id);
        this.cache.onStateUpdate(this);
      }
      users = this.cache.currentPage.map(({ follower }) => follower);
      title = 'Comment Likes';
      noneFoundText = 'No likes yet';
    } else if (content === 'CollectionSubscribers') {
      if (!this.cache) {
        this.cache = getCollectionSubscribers(id);
        this.cache.onStateUpdate(this);
      }
      users = this.cache.currentPage.map(({ follower }) => follower);
      title = 'Collection Subscribers';
      noneFoundText = 'No subscribers yet';
      collection = LogicBaseCollectionCache.get(id);
      if (collection) {
        const isOwner = isLoggedIn && collection.owner === account._id;
        const isContributor = !isOwner && collection.editors.find((_id) => _id === account._id);
        canRemoveSubscriber = isOwner;
        if (!(isOwner || isContributor)) collection = null;
      }
    } else if (content === 'Followers') {
      if (!this.cache) {
        this.cache = getUserFollowers(id);
        this.cache.onStateUpdate(this);
      }
      users = this.cache.currentPage.map(({ follower }) => follower);
      title = 'Followers';
      noneFoundText = 'No followers yet';
      if (account._id === id) followee = id;
    } else if (content === 'Following') {
      if (!this.cache) {
        this.cache = getUserFollowees(id);
        this.cache.onStateUpdate(this);
      }
      users = this.cache.currentPage.map(({ followee: f }) => f);
      title = 'Following';
      noneFoundText = 'Not following anyone yet';
    } else {
      if (!this.cache) {
        this.cache = content === 'AppSaves' && getAppPins(id) || getAppLikes(id);
        this.cache.onStateUpdate(this);
      }
      users = this.cache.currentPage.map(({ follower }) => follower);
      title = content === 'AppSaves' && 'Pins' || 'Likes';
      noneFoundText = content === 'AppSaves' && 'No pins yet' || 'No likes yet';
    }
    const pageReady = !!(this.cache.currentPage && (this.cache.currentPage.length > 0 || this.cache.allFound));
    if (pageReady) {
      this.users = UserCache.getUsersByIds(users) || this.users;
    }
    const usersReady = pageReady && (!users.length || !!this.users);
    return (
      <>
        <Popup
          onClose={onClose}
          allowBackgroundClose
          showCloseButton
        >
          <Shell>
            <Header>
              <ModalSheetTitle style={{ margin: 0 }}>{title}</ModalSheetTitle>
            </Header>
            <Scrollable
              vertical
              paginationHeight={200}
              onPagination={this.onPagination}
              popupMaxHeight
              style={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                position: 'relative',
                maxHeight: 'calc(min(85vh, 640px) - 5.25rem)',
                ...(!usersReady ? {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                } : {}),
              }}
            >
              {!usersReady ? (
                <LoadingIndicator />
              ) : (
                <ListWrap>
                  {this.users && this.users.length > 0 ? this.users.map((user) => (
                    <UserRowCard key={user._id}>
                      <RowMain>
                        <UserProfileHeader
                          user={user}
                          small
                          profileFullWidth
                          hideFollow={!!(collection || followee)}
                        />
                      </RowMain>
                      {((collection && canRemoveSubscriber) || followee) && (
                        <RemoveButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (collection) this.setState({ showRemoveSubscriberPopup: user });
                            else if (followee) this.setState({ showRemoveFollowerPopup: user });
                          }}
                        >
                          Remove
                        </RemoveButton>
                      )}
                    </UserRowCard>
                  )) : (
                    <EmptyState>{noneFoundText}</EmptyState>
                  )}
                </ListWrap>
              )}
            </Scrollable>
          </Shell>
        </Popup>
        {showRemoveSubscriberPopup && <ConfirmDialog
          text="Confirm Remove"
          description="Are you sure you wish to remove the subscriber?"
          open
          onNo={() => this.setState({ showRemoveSubscriberPopup: false })}
          onYes={() => {
            this.setState({ showRemoveSubscriberPopup: false });
            setTimeout(() => {
              const subscription = this.cache.currentPage.find(({ follower }) => showRemoveSubscriberPopup._id === follower);
              LogicBaseCollectionSubscriptionCache.__deleteQueryFromCache({ fr: showRemoveSubscriberPopup._id, c: collection._id });
              LogicBaseCollectionSubscriptionCache.delete(subscription, true);
            });
            collection.followers -= 1;
          }}
        />}
        {showRemoveFollowerPopup && <ConfirmDialog
          text="Confirm Remove"
          description="Are you sure you wish to remove the follower?"
          open
          onNo={() => this.setState({ showRemoveFollowerPopup: false })}
          onYes={() => {
            this.setState({ showRemoveFollowerPopup: false });
            setTimeout(() => {
              const relationship = this.cache.currentPage.find(({ follower }) => showRemoveFollowerPopup._id === follower);
              LogicBaseRelationshipCache.__deleteQueryFromCache({ fe: UserAccount.account._id, fr: showRemoveFollowerPopup._id });
              LogicBaseRelationshipCache.delete(relationship, true);
            });
            const theirSocial = LogicBaseUserCache.get(showRemoveFollowerPopup._id);
            const yourSocial = LogicBaseUserCache.get(UserAccount.account._id);
            if (theirSocial) theirSocial.followees -= 1;
            if (yourSocial) yourSocial.followers -= 1;
          }}
        />}
      </>
    );
  }
}
